import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Input,
  Message,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
} from '@arco-design/web-react'
import { IconDelete, IconEdit, IconPlus, IconRefresh, IconSearch, IconSync } from '@arco-design/web-react/icon'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  deleteModelMetaApi,
  getMissingModelsApi,
  listModelsMetaApi,
  listVendorsApi,
  searchModelsMetaApi,
  updateModelStatusOnlyApi,
  type ModelMeta,
  type VendorMeta,
} from '@/api/models'
import ModelFormModal, { type ModelFormSeed } from '@/components/models/ModelFormModal'
import MissingModelsModal from '@/components/models/MissingModelsModal'
import SyncUpstreamModal from '@/components/models/SyncUpstreamModal'
import VendorManagerModal from '@/components/models/VendorManagerModal'
import { Pill, renderChannelType, renderModelFeatureTags, renderModelTag, renderTimestamp } from '@/utils/render'
import { PAGE_SIZE_OPTIONS } from '@/utils/constants'

const ALL = 'all'

async function loadModelsPage(params: {
  page: number
  pageSize: number
  keyword: string
  vendor: string
}): ReturnType<typeof listModelsMetaApi> {
  const { page, pageSize, keyword, vendor } = params
  const kw = keyword.trim()
  if (kw || (vendor && vendor !== ALL)) {
    return searchModelsMetaApi({
      p: page,
      page_size: pageSize,
      ...(kw ? { keyword: kw } : {}),
      ...(vendor && vendor !== ALL ? { vendor: Number(vendor) } : {}),
    })
  }
  return listModelsMetaApi({ p: page, page_size: pageSize })
}

function renderBoundChannels(row: ModelMeta) {
  const channels = row.bound_channels || []
  if (!channels.length) return <span className="text-xs text-gray-400">未绑定</span>
  const shown = channels.slice(0, 2)
  return (
    <div className="flex min-w-[160px] flex-col gap-2">
      {shown.map((channel) => (
        <div key={`${channel.name}-${channel.type}`} className="min-w-0">
          <Tooltip content={channel.name}>
            <div className="max-w-[150px] truncate text-sm font-medium text-slate-800">{channel.name}</div>
          </Tooltip>
          <div className="mt-1">{renderChannelType(channel.type)}</div>
        </div>
      ))}
      {channels.length > shown.length && <Pill variant="neutral">+{channels.length - shown.length}</Pill>}
    </div>
  )
}

export default function ModelPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [searchKey, setSearchKey] = useState({ keyword: '', vendor: ALL })
  const [pendingId, setPendingId] = useState<number | null>(null)
  const [formSeed, setFormSeed] = useState<ModelFormSeed | null>(null)
  const [vendorOpen, setVendorOpen] = useState(false)
  const [syncOpen, setSyncOpen] = useState(false)
  const [missingOpen, setMissingOpen] = useState(false)
  const tableAreaRef = useRef<HTMLDivElement>(null)
  const [tableBodyScrollY, setTableBodyScrollY] = useState(440)

  const qc = useQueryClient()
  const measureTableBody = useCallback(() => {
    const el = tableAreaRef.current
    if (!el) return
    const theadReserve = 50
    setTableBodyScrollY(Math.max(240, Math.floor(el.clientHeight - theadReserve)))
  }, [])

  useLayoutEffect(() => {
    measureTableBody()
    const el = tableAreaRef.current
    const ro = new ResizeObserver(() => measureTableBody())
    if (el) ro.observe(el)
    window.addEventListener('resize', measureTableBody)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measureTableBody)
    }
  }, [measureTableBody])

  const vendorsQuery = useQuery({
    queryKey: ['vendors-meta', 'all'],
    queryFn: async () => {
      const data = (await listVendorsApi({ page_size: 1000 })) as { items?: VendorMeta[] }
      return data.items || []
    },
  })

  const missingQuery = useQuery({
    queryKey: ['models-missing'],
    queryFn: getMissingModelsApi,
    staleTime: 60_000,
  })

  const listQuery = useQuery({
    queryKey: ['models-meta', { page, pageSize, ...searchKey }],
    queryFn: () => loadModelsPage({ page, pageSize, keyword: searchKey.keyword, vendor: searchKey.vendor }),
  })

  const vendorNameMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const vendor of vendorsQuery.data || []) {
      map.set(vendor.id, vendor.name)
    }
    return map
  }, [vendorsQuery.data])

  const handleSearch = () => {
    setPage(1)
    setSearchKey({ keyword, vendor: searchKey.vendor })
  }

  const handleVendorChange = (v: string | undefined) => {
    const next = v || ALL
    setSearchKey((s) => ({ ...s, vendor: next }))
    setPage(1)
  }

  const refreshAll = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['models-meta'] }),
      qc.invalidateQueries({ queryKey: ['vendors-meta'] }),
      qc.invalidateQueries({ queryKey: ['models-missing'] }),
    ])
  }

  const doToggle = async (row: ModelMeta, next: boolean) => {
    setPendingId(row.id)
    try {
      await updateModelStatusOnlyApi(row.id, next ? 1 : 0)
      Message.success(next ? '已启用' : '已禁用')
      await qc.invalidateQueries({ queryKey: ['models-meta'] })
    } catch (e) {
      Message.error(e instanceof Error ? e.message : '操作失败')
    } finally {
      setPendingId(null)
    }
  }

  const doDelete = async (id: number) => {
    try {
      await deleteModelMetaApi(id)
      Message.success('已删除')
      await refreshAll()
    } catch (e) {
      Message.error(e instanceof Error ? e.message : '删除失败')
    }
  }

  const items = listQuery.data?.items || []
  const total = listQuery.data?.total ?? 0
  const missingCount = missingQuery.data?.length ?? 0

  useLayoutEffect(() => {
    if (!listQuery.isLoading) measureTableBody()
  }, [items.length, listQuery.isLoading, measureTableBody])

  const columns = [
    {
      title: '模型',
      dataIndex: 'model_name',
      width: 280,
      render: (v: string, row: ModelMeta) => (
        <div className="space-y-1">
          {v ? renderModelTag(v) : '-'}
          {row.tags && renderModelFeatureTags(row.tags, 4)}
        </div>
      ),
    },
    {
      title: '供应商',
      width: 190,
      render: (_: unknown, row: ModelMeta) => {
        const name = row.vendor_id ? vendorNameMap.get(row.vendor_id) : undefined
        return (
          <div className="min-w-[150px]">
            {name ? (
              <Pill variant="info" className="max-w-full truncate" title={name}>
                {name}
              </Pill>
            ) : row.vendor_id ? (
              <Pill variant="warning">#{row.vendor_id}</Pill>
            ) : (
              '-'
            )}
          </div>
        )
      },
    },
    {
      title: '绑定渠道',
      width: 190,
      render: (_: unknown, row: ModelMeta) => renderBoundChannels(row),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: number) =>
        status === 1 ? <Pill variant="success">已启用</Pill> : <Pill variant="neutral">已禁用</Pill>,
    },
    {
      title: '同步',
      width: 110,
      render: (_: unknown, row: ModelMeta) =>
        row.sync_official === 0 ? <Pill variant="neutral">本地维护</Pill> : <Pill variant="success">官方同步</Pill>,
    },
    { title: '更新时间', dataIndex: 'updated_time', width: 170, render: (t: number) => (t ? renderTimestamp(t) : '-') },
    {
      title: '操作',
      width: 190,
      fixed: 'right' as const,
      render: (_: unknown, row: ModelMeta) => (
        <Space size={8}>
          <Tooltip content="编辑">
            <Button
              size="small"
              type="text"
              icon={<IconEdit />}
              aria-label="编辑模型"
              onClick={() => setFormSeed({ id: row.id })}
            />
          </Tooltip>
          <Tooltip content={row.status === 1 ? '禁用模型' : '启用模型'}>
            <Switch
              size="small"
              checked={row.status === 1}
              loading={pendingId === row.id}
              onChange={(checked) => {
                if (checked === (row.status === 1)) return
                doToggle(row, checked)
              }}
            />
          </Tooltip>
          <Popconfirm title="确定删除？此操作不可恢复" onOk={() => doDelete(row.id)}>
            <Button size="small" type="text" status="danger" icon={<IconDelete />} aria-label="删除模型" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <Card className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="模型名关键词"
              value={keyword}
              onChange={setKeyword}
              style={{ width: 240 }}
              onPressEnter={handleSearch}
              prefix={<IconSearch />}
              allowClear
            />
            <Select
              placeholder="全部供应商"
              value={searchKey.vendor}
              onChange={handleVendorChange}
              style={{ width: 220 }}
              loading={vendorsQuery.isLoading}
              allowClear
            >
              <Select.Option value={ALL}>全部供应商</Select.Option>
              {(vendorsQuery.data || []).map((vendor) => (
                <Select.Option key={vendor.id} value={String(vendor.id)}>
                  {vendor.name}
                </Select.Option>
              ))}
            </Select>
            <Button type="primary" icon={<IconSearch />} onClick={handleSearch}>
              查询
            </Button>
            <Button icon={<IconRefresh />} loading={listQuery.isFetching && !listQuery.isLoading} onClick={() => listQuery.refetch()}>
              刷新
            </Button>
          </div>
          <Space wrap>
            <Button type="primary" icon={<IconPlus />} onClick={() => setFormSeed({})}>
              添加模型
            </Button>
            <Button onClick={() => setVendorOpen(true)}>供应商管理</Button>
            <Button onClick={() => setMissingOpen(true)}>
              未配置模型{missingCount > 0 ? ` (${missingCount})` : ''}
            </Button>
            <Button type="primary" icon={<IconSync />} onClick={() => setSyncOpen(true)}>
              同步上游
            </Button>
          </Space>
        </div>
      </Card>

      <Card
        className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)] flex h-[calc(100vh-14rem)] min-h-[300px] flex-col"
        bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden', height: '100%' }}
      >
        <div ref={tableAreaRef} className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <Table
            rowKey="id"
            loading={listQuery.isLoading}
            columns={columns}
            data={items}
            pagination={false}
            noDataElement={
              <div className="py-10">
                <Empty description="暂无模型元数据，或当前筛选条件没有匹配结果" />
              </div>
            }
            scroll={{ x: 1280, y: tableBodyScrollY }}
            borderCell={false}
            stripe={false}
            className="[&_.arco-table-container]:max-w-full [&_.arco-table-container]:overflow-x-auto [&_.arco-table-th]:!py-3 [&_.arco-table-td]:!py-3"
          />
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-2 rounded-b-2xl border-t border-[#EAECEF] bg-[#FAFBFC] px-4 py-3">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showTotal={(count, range) => (count > 0 ? `第 ${range[0]}-${range[1]} 条，共 ${count} 条` : `共 ${count} 条`)}
            sizeCanChange
            pageSizeChangeResetCurrent
            sizeOptions={PAGE_SIZE_OPTIONS}
            onChange={(p, ps) => {
              setPage(p)
              setPageSize(ps)
            }}
          />
        </div>
      </Card>

      <ModelFormModal
        visible={!!formSeed}
        seed={formSeed}
        onCancel={() => setFormSeed(null)}
        onSuccess={refreshAll}
      />

      <VendorManagerModal visible={vendorOpen} onCancel={() => setVendorOpen(false)} />

      <SyncUpstreamModal
        visible={syncOpen}
        onCancel={() => setSyncOpen(false)}
        onSuccess={refreshAll}
      />

      <MissingModelsModal
        visible={missingOpen}
        onCancel={() => setMissingOpen(false)}
        onSuccess={refreshAll}
      />
    </div>
  )
}
